import React from 'react';
import { Link } from 'react-router-dom';

// El panel de los dos homes (retail y cafetería). Es el mismo componente a propósito:
// los dos modos se operan con el mismo esqueleto, así que quien aprende uno ya sabe el
// otro, y una diferencia visual entre ellos sería ruido y no información.
//
// Un panel = un área de trabajo de UNA persona. Esa es la regla que hace que el tablero
// escale: una señal nueva se cuelga del panel del área que ya le corresponde en vez de
// pedir un panel propio. Diez paneles no entran en la pantalla; diez filas repartidas
// entre cuatro paneles, sí.

const COLOR = {
    texto: '#3d5170',
    apagado: '#5a6673',
    alerta: '#c4183c',
    aviso: '#8a5a00',
    ok: '#12915a',
    borde: '#e1e5eb',
    bordeSuave: '#f0f2f5',
    accion: '#007bff',
};

// El contador del encabezado. Rojo cuando lo pendiente nos espera a nosotros, ámbar
// cuando espera a un tercero, gris cuando es sólo un número.
function Contador({ valor, tono }) {
    if (valor === null || valor === undefined) {
        return null;
    }
    const fondo = tono === 'alerta' ? COLOR.alerta : tono === 'aviso' ? COLOR.aviso : '#eef1f5';
    const color = tono === 'alerta' || tono === 'aviso' ? '#ffffff' : COLOR.texto;
    return (
        <span style={{ fontSize: 11, fontWeight: 700, color, background: fondo, borderRadius: 10, padding: '2px 8px', marginLeft: 8 }}>
            {valor}
        </span>
    );
}

export function PanelHome({ icono, titulo, contador, tono, verTodo, verTodoTexto, children, pie, cargando }) {
    return (
        <div className="card card-small h-100" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLOR.borde}`, display: 'flex', alignItems: 'center' }}>
                <i className="material-icons" style={{ fontSize: 18, color: COLOR.texto, marginRight: 9 }}>{icono}</i>
                <span style={{ fontSize: 15, fontWeight: 700, color: COLOR.texto }}>{titulo}</span>
                <Contador valor={contador} tono={tono} />
                <span style={{ flexGrow: 1 }} />
                {verTodo ? (
                    <Link to={verTodo} style={{ fontSize: 12 }}>{verTodoTexto || 'Ver todo'}</Link>
                ) : null}
            </div>
            <div style={{ flexGrow: 1 }}>
                {cargando ? <Vacio texto="…" /> : children}
            </div>
            {pie ? (
                <div style={{ padding: '11px 16px', borderTop: `1px solid ${COLOR.borde}` }}>{pie}</div>
            ) : null}
        </div>
    );
}

// Un bloque en null NO es "no hay nada": es "esta consulta falló" (el backend arma cada
// bloque con su propio try/catch para que un panel roto no tumbe la puerta de entrada).
// Por eso el texto distingue los dos casos en vez de mostrar un cero tranquilizador.
export function Vacio({ texto }) {
    return (
        <div style={{ padding: '18px 16px', fontSize: 13, color: COLOR.apagado }}>{texto}</div>
    );
}

export function SubTitulo({ texto, tono }) {
    // `salida` marca el grupo cuyo trabajo implica salir del local. No es una alarma —el
    // rojo ya lo usa lo atrasado— pero tampoco es un rótulo más: es la diferencia entre
    // dejar algo preparado en el mostrador y tener que ir hasta la agencia.
    const color = tono === 'ok' ? COLOR.ok : tono === 'salida' ? COLOR.aviso : COLOR.apagado;
    return (
        <div style={{
            padding: '10px 16px 5px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
            color,
        }}>
            {texto}
        </div>
    );
}

// Una fila de trabajo: lo que hay que hacer a la izquierda, cuánto hace que espera en el
// medio, y el botón que lo resuelve a la derecha. El botón va SIEMPRE, aunque la acción
// sea sólo navegar: una fila sin salida obliga a buscar la pantalla a mano.
// `urgente` y `destacada` son cosas DISTINTAS y por eso son dos props. `urgente` dice que
// la fila está atrasada y pinta la antigüedad de rojo. `destacada` dice que esa acción es
// LA acción del panel y llena el botón de azul.
//
// Estaban atados —el botón se llenaba cuando la fila era urgente— y el resultado era que
// dos filas con el mismo botón "Despachado" se veían distintas según la antigüedad, como
// si fueran dos acciones diferentes. Lo atrasado ya se dice en la fecha, que es donde
// corresponde; el botón dice qué se puede hacer, y eso no cambia con el reloj.
export function Fila({ titulo, detalle, meta, urgente, destacada, accion, onAccion, href, atributos, primero }) {
    const borde = primero ? {} : { borderTop: `1px solid ${COLOR.bordeSuave}` };
    return (
        <div style={{ display: 'flex', alignItems: 'center', padding: '9px 16px', ...borde }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLOR.texto, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {titulo}
                </div>
                {detalle ? (
                    <div style={{ fontSize: 11, color: urgente ? COLOR.alerta : COLOR.apagado, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {detalle}
                    </div>
                ) : null}
            </div>
            {meta ? (
                <div style={{ width: 86, flexShrink: 0, fontSize: 11, textAlign: 'right', marginRight: 10, color: urgente ? COLOR.alerta : COLOR.apagado, fontWeight: urgente ? 600 : 400 }}>
                    {meta}
                </div>
            ) : null}
            {accion ? <Boton texto={accion} onClick={onAccion} href={href} destacado={destacada} atributos={atributos} /> : null}
        </div>
    );
}

// Navegar es un <Link> y resolver es un <button>: si todo fuera un div con onClick, el
// Tab se los saltea y no se puede operar con el teclado — que es como se trabaja en el
// mostrador con una mano en el lector de código.
// `atributos` deja pasar los `data-*` que necesitan los modales del backoffice, que son de
// Bootstrap + jQuery y se abren por `data-toggle`/`data-target` y no por estado de React.
export function Boton({ texto, onClick, href, destacado, atributos }) {
    const estilo = {
        fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '6px 12px', flexShrink: 0,
        border: destacado ? 'none' : `1px solid #d5dbe3`,
        background: destacado ? COLOR.accion : '#ffffff',
        color: destacado ? '#ffffff' : COLOR.texto,
        textDecoration: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
    };
    if (href) {
        return <Link to={href} style={estilo}>{texto}</Link>;
    }
    return <button type="button" style={estilo} onClick={onClick} {...(atributos || {})}>{texto}</button>;
}

// La tira de abajo: lo que casi siempre está en cero. En verde ocupa una línea; cuando
// hay algo se pone roja. Si fueran paneles, estas tres señales empujarían fuera de la
// pantalla a las que sí se usan todos los días.
export function TiraRevisar({ items }) {
    return (
        <div className="card card-small mb-4">
            <div style={{ padding: '11px 16px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 22 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: COLOR.apagado, letterSpacing: '0.6px', flexShrink: 0 }}>REVISAR</span>
                {items.map((item) => (
                    <div key={item.titulo} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        {item.alerta ? (
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.tono === 'aviso' ? COLOR.aviso : COLOR.alerta, display: 'inline-block' }} />
                        ) : (
                            <i className="material-icons" style={{ fontSize: 16, color: COLOR.ok }}>check</i>
                        )}
                        <span style={{ fontSize: 13, color: COLOR.texto, fontWeight: item.alerta ? 600 : 400 }}>{item.titulo}</span>
                        {item.alerta && item.ruta ? <Link to={item.ruta} style={{ fontSize: 12, fontWeight: 600 }}>{item.accion || 'Ver'}</Link> : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

export { COLOR };
